import {
  fetchPrivacyOpsConfigStatus,
  mapPrivacyOpsConfigStatus,
  mapQueryDsrSummary,
  mapQueryDsrTypesSummary,
  mapQueryNeedAttentionData,
  mapQueryPiaAssessmentsBasedOnRiskReviews,
  mapQueryPiaSummary,
  mapQueryProcessBasedOnGroups,
  mapQueryRisksBasedOnGroups,
  mapQueryRopaSummary,
  queryDsrSummary,
  queryDsrTypesSummary,
  queryNeedAttentionData,
  queryPiaAssessmentsBasedOnRiskReviews,
  queryPiaSummary,
  queryProcessBasedOnGroups,
  queryRisksBasedOnGroups,
  queryRopaSummary
} from './queries'
import graphqlService from '../../services/graphqlService'
import apiService from '../../services/api/apiService'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

export type DsrSummary = {
  openRequests?: number
  totalRequests?: number
  openTickets?: number
  totalTickets?: number
  requestsNeedAttention?: number
  automationFailures?: number
  requestsPastDue?: number
}

export type NeedAttentionData = {
  dsrCount?: number
  piaAssessmentCount?: number
  ropaProcessCount?: number
}

export type RopaSummary = {
  totalProcesses?: number
  processesNeedAttention?: number
  unmappedAttributeTypes?: number
}

export type DsrTypeCount = {
  type: string
  count: number
}

export type ProcessGroupCount = {
  id: string
  name: string
  count: number
}

export type RiskGroupCount = {
  id: string
  name: string
  count: number
}

export type RiskAssessmentsCounts = {
  id: string
  riskType: string
  count: number
  color: string
}[]

export type PiaSummary = {
  assessmentsCount: number
  pendingReviewAssessmentsCount: number
}

export type CookieDomainCounters = {
  domainsCount: number
  totalCookies: number
  totalNewCookies: number
  newScriptsCount: number
}

export type DsrRequestsCount = {
  inProgress: number
  awaitingApproval: number
  closed: number
}

export type PrivacyOpsOverviewState = {
  isPrivacyOpsConfigured: boolean
  dsrSummary: DsrSummary
  dsrRequestsCount?: DsrRequestsCount
  needAttentionData: NeedAttentionData
  ropaSummary: RopaSummary
  dsrTypesSummary: DsrTypeCount[]
  risksBasedOnGroups: RiskGroupCount[]
  processBasedOnGroups: ProcessGroupCount[]
  totalRopaProcesses: number
  totalPiaProcesses: number
  piaRiskAssessmentsCounts?: RiskAssessmentsCounts
  piaSummary?: PiaSummary
  cookieDomainCounters?: any
}

export const ACTION_PRIVACY_OPS_CONFIGURE = 'privacyOps/configure'
export const fetchPrivacyOpsConfigDetails = createAsyncThunk(
  ACTION_PRIVACY_OPS_CONFIGURE,
  async () => {
    const raw = await graphqlService.execute(fetchPrivacyOpsConfigStatus())
    const domains = await apiService.getCookieConsentDomains()
    const isRestPrivacyOpsModuleConfigured = mapPrivacyOpsConfigStatus(raw)
    const isPrivacyOpsConfigured =
      isRestPrivacyOpsModuleConfigured.isPrivacyOpsConfigured || domains.total > 0
    return isPrivacyOpsConfigured
  }
)

export const ACTION_FETCH_DSR_SUMMARY = 'privacyOps/dsr/summary'
export const fetchDsrSummary = createAsyncThunk(
  ACTION_FETCH_DSR_SUMMARY,
  async (timePeriod: string) => {
    const raw = await graphqlService.execute(queryDsrSummary(timePeriod))
    return mapQueryDsrSummary(raw)
  }
)

export const ACTION_FETCH_NEED_ATTENTION_DATA = 'privacyOps/needAttention'
export const fetchNeedAttentionData = createAsyncThunk(
  ACTION_FETCH_NEED_ATTENTION_DATA,
  async (timePeriod: string) => {
    const raw = await graphqlService.execute(queryNeedAttentionData(timePeriod))
    return mapQueryNeedAttentionData(raw)
  }
)

export const ACTION_FETCH_ROPA_SUMMARY = 'privacyOps/ropa/summary'
export const fetchRopaSummary = createAsyncThunk(
  ACTION_FETCH_ROPA_SUMMARY,
  async (timePeriod: string) => {
    const raw = await graphqlService.execute(queryRopaSummary(timePeriod))
    return mapQueryRopaSummary(raw)
  }
)

export const ACTION_FETCH_DSR_TYPES_SUMMARY = 'privacyOps/dsr/types/summary'
export const fetchDsrTypesSummary = createAsyncThunk(
  ACTION_FETCH_DSR_TYPES_SUMMARY,
  async (timePeriod: string) => {
    const raw = await graphqlService.execute(queryDsrTypesSummary(timePeriod))
    return mapQueryDsrTypesSummary(raw)
  }
)

export const ACTION_FETCH_RISK_BASED_ON_GROUPS = 'privacyOps/risk/groups'
export const fetchRiskBasedOnGroups = createAsyncThunk(
  ACTION_FETCH_RISK_BASED_ON_GROUPS,
  async (timePeriod: string) => {
    const raw = await graphqlService.execute(queryRisksBasedOnGroups(timePeriod))
    return mapQueryRisksBasedOnGroups(raw)
  }
)

export const ACTION_FETCH_PROCESS_BASED_ON_GROUPS = 'privacyOps/process/groups'
export const fetchProcessBasedOnGroups = createAsyncThunk(
  ACTION_FETCH_PROCESS_BASED_ON_GROUPS,
  async (timePeriod: string) => {
    const raw = await graphqlService.execute(queryProcessBasedOnGroups(timePeriod))
    return mapQueryProcessBasedOnGroups(raw)
  }
)

export const ACTION_FETCH_PIA_ASSESSMENTS_BASED_ON_RISK_REVIEWS = 'privacyOps/pia/riskReviews'
export const fetchPiaAssessmentsBasedOnRiskReviews = createAsyncThunk(
  ACTION_FETCH_PIA_ASSESSMENTS_BASED_ON_RISK_REVIEWS,
  async (timePeriod: string) => {
    const raw = await graphqlService.execute(queryPiaAssessmentsBasedOnRiskReviews(timePeriod))
    return mapQueryPiaAssessmentsBasedOnRiskReviews(raw)
  }
)

export const ACTION_FETCH_PIA_SUMMARY = 'privacyOps/pia/summary'
export const fetchPiaSummary = createAsyncThunk(
  ACTION_FETCH_PIA_SUMMARY,
  async (timePeriod: string) => {
    const raw = await graphqlService.execute(queryPiaSummary(timePeriod))
    return mapQueryPiaSummary(raw)
  }
)

export const ACTION_FETCH_COOKIE_DOMAIN_COUNTERS = 'privacyOps/cookieConsent/domain/counters'
export const fetchCookieDomainCounters = createAsyncThunk(
  ACTION_FETCH_COOKIE_DOMAIN_COUNTERS,
  async () => {
    return await apiService.getCookieConsentDomainsCounters()
  }
)

const initialState: PrivacyOpsOverviewState = {
  isPrivacyOpsConfigured: false,
  dsrSummary: {},
  needAttentionData: {},
  ropaSummary: {},
  dsrTypesSummary: [],
  risksBasedOnGroups: [],
  processBasedOnGroups: [],
  totalRopaProcesses: 0,
  totalPiaProcesses: 0
}

const privacyOpsOverviewSlice = createSlice({
  name: 'privacyOpsOverview',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchPrivacyOpsConfigDetails.fulfilled, (state, action) => {
      state.isPrivacyOpsConfigured = action.payload
    })
    builder.addCase(fetchDsrSummary.fulfilled, (state, action) => {
      state.dsrSummary = action.payload
    })
    builder.addCase(fetchNeedAttentionData.fulfilled, (state, action) => {
      state.needAttentionData = action.payload
    })
    builder.addCase(fetchRopaSummary.fulfilled, (state, action) => {
      state.ropaSummary = action.payload
    })
    builder.addCase(fetchDsrTypesSummary.fulfilled, (state, action) => {
      state.dsrTypesSummary = action.payload.list
      state.dsrRequestsCount = action.payload.counts
    })
    builder.addCase(fetchRiskBasedOnGroups.fulfilled, (state, action) => {
      state.risksBasedOnGroups = action.payload.list
      state.totalPiaProcesses = action.payload.total
    })
    builder.addCase(fetchProcessBasedOnGroups.fulfilled, (state, action) => {
      state.processBasedOnGroups = action.payload.list
      state.totalRopaProcesses = action.payload.total
    })
    builder.addCase(fetchPiaAssessmentsBasedOnRiskReviews.fulfilled, (state, action) => {
      state.piaRiskAssessmentsCounts = action.payload
    })
    builder.addCase(fetchPiaSummary.fulfilled, (state, action) => {
      state.piaSummary = action.payload
    })
    builder.addCase(fetchCookieDomainCounters.fulfilled, (state, action) => {
      const domains = action.payload

      let totalCookies = 0
      let totalNewCookies = 0
      let newScriptsCount = 0

      domains.forEach((domain) => {
        totalCookies += domain.cookiesCount + domain.newCookiesCount
        totalNewCookies += domain.newCookiesCount
        if (domain.scriptVersion) newScriptsCount += 1
      })
      state.cookieDomainCounters = {
        domainsCount: domains.length,
        totalCookies,
        totalNewCookies,
        newScriptsCount
      }
    })
  }
})

// export const {} = privacyOpsOverviewSlice.actions

export default privacyOpsOverviewSlice.reducer
